import React, { useCallback, useEffect, useState } from "react";
import { Accordion, Segment } from "semantic-ui-react";
import "./index.scss";
import { useDispatch, useSelector } from "react-redux";
import pbx_config from "actions/pbx_config";
import ExternalNumbers from "pages/PhoneManagement/Numbers/ExternalNumbers/subcomponents/Table";
import DylNumbers from "pages/PhoneManagement/Numbers/DylNumbers/subcomponents/Table";
import UserExtensionsTable from "pages/PhoneManagement/Extensions/Users/subcomponents/UserExtensionsTable";
import useModal from "utils/useModal";
import VerifyExternalNumberModal from "shared/modals/VerifyExternalNumberModal";
import UserExtensionModal from "shared/modals/UserExtensionModal";
import AudioModal from "shared/modals/Audio/Audio";
import { PhoneUtil } from "utils";
import { useParams } from "react-router-dom";
import { USER_PERMISSIONS } from "dyl-components";
import RestrictedView from "shared/RestrictedView";

const accordionIconUp = {
    className: `fas fa-circle-chevron-up`,
    color: "primary",
    size: "large",
};

const accordionIconDown = {
    className: `fas fa-circle-chevron-down`,
    color: "primary",
    size: "large",
};

const PhoneTab = () => {
    const dispatch = useDispatch();
    const { id: user_id } = useParams();

    const [activeIndex, setActiveIndex] = useState([0, 1, 2]);
    const userPhoneCreds = useSelector((state) => state?.officeView?.creds);
    const { phoneTab, isReadingPhoneTab } = useSelector(
        (state) => state?.pbx_config
    );
    const isPhoneSystemSupported =
        PhoneUtil.isPhoneSystemSupported(userPhoneCreds);
    const verifyPhoneModal = useModal();
    const editExtenstionModal = useModal();
    const addVoicemailGreetingModal = useModal();

    const selectVerifyingPhone = (number) => {
        verifyPhoneModal?.openModal?.(number);
    };

    const onEditExtension = async (extension) => {
        await dispatch(pbx_config.getExtension(extension));
        editExtenstionModal?.openModal();
    };

    const onAddVoicemailGreeting = async (data) => {
        const { sound_id } = data;
        await dispatch(pbx_config.getSound(sound_id));
        addVoicemailGreetingModal?.openModal(data);
    };

    const handleAccordionClick = useCallback(
        (e, titleProps) => {
            const { index } = titleProps;
            const dataMap = {
                0: phoneTab?.user_extensions,
                1: phoneTab?.dyl_numbers,
                2: phoneTab?.external_numbers,
            };
            const panelData = dataMap[index];
            const hasData = panelData?.length > 0;

            if (!hasData) return;

            setActiveIndex((preState) => {
                const newIndex = preState?.includes?.(index)
                    ? preState?.filter?.((item) => item !== index)
                    : [...preState, index];
                return newIndex;
            });
        },
        [
            phoneTab?.dyl_numbers,
            phoneTab?.external_numbers,
            phoneTab?.user_extensions,
        ]
    );

    const onReload = () => {
        dispatch(pbx_config.getPhoneTab({ user_id }));
    };

    useEffect(() => {
        if (!user_id) return;
        dispatch(pbx_config.getPhoneTab({ user_id }));
    }, [dispatch, user_id]);

    useEffect(() => {
        if (isReadingPhoneTab) return;

        const newActiveIndexes = [];

        if (isPhoneSystemSupported) {
            if (phoneTab?.user_extensions?.length) newActiveIndexes.push(0);
        }

        if (phoneTab?.dyl_numbers?.length) newActiveIndexes.push(1);
        if (phoneTab?.external_numbers?.length) newActiveIndexes.push(2);

        setActiveIndex(newActiveIndexes);
    }, [phoneTab, isReadingPhoneTab, isPhoneSystemSupported]);

    return (
        <React.Fragment>
            <RestrictedView userPermission={USER_PERMISSIONS.USER_PROFILE_PREFERENCES_VIEW} type="restricted" userRestrict>
                <VerifyExternalNumberModal
                    open={verifyPhoneModal?.isVisible}
                    onClose={verifyPhoneModal?.closeModal}
                    verifyingPhone={verifyPhoneModal?.data}
                    onReload={onReload}
                />
                <UserExtensionModal
                    open={editExtenstionModal?.isVisible}
                    onClose={editExtenstionModal?.closeModal}
                    onReload={onReload}
                />
                <AudioModal
                    onReload={onReload}
                    onClose={addVoicemailGreetingModal?.closeModal}
                    open={addVoicemailGreetingModal?.isVisible}
                    title="Add New Voicemail Greeting"
                    category="personal_voicemail"
                    defaultApplyTo={[addVoicemailGreetingModal?.data?.voicemail_id]}
                    readonlyFields={["sound_category", "apply_to"]}
                    user_extension={
                        addVoicemailGreetingModal?.data?.extension_number
                    }
                    isAddGreetingModal
                />
                <Accordion
                    fluid
                    className="PhoneTabAccordian"
                    activeIndex={activeIndex}
                    onTitleClick={handleAccordionClick}
                    exclusive={false}
                    panels={[
                        isPhoneSystemSupported && {
                            key: "userExtension",
                            title: {
                                content: (
                                    <span
                                        className={`AccordianTitle ${
                                            !phoneTab?.user_extensions?.length &&
                                            `disabled`
                                        }`}
                                    >
                                        User Extension
                                    </span>
                                ),
                                icon: activeIndex?.includes?.(0)
                                    ? accordionIconUp
                                    : Object.assign(accordionIconDown, {
                                        disabled:
                                            !phoneTab?.user_extensions?.length,
                                    }),
                            },
                            content: {
                                content: (
                                    <Segment basic className="UserExtensions">
                                        <UserExtensionsTable
                                            isPhoneTab
                                            userExtensions={
                                                phoneTab?.user_extensions
                                            }
                                            count={
                                                phoneTab?.user_extensions?.length
                                            }
                                            isReadingUserExtensions={
                                                isReadingPhoneTab
                                            }
                                            onOpenModal={onEditExtension}
                                            onReload={onReload}
                                            onAddVoicemailGreeting={
                                                onAddVoicemailGreeting
                                            }
                                        />
                                    </Segment>
                                ),
                            },
                        },
                        {
                            key: "dylNumbers",
                            title: {
                                content: (
                                    <span
                                        className={`AccordianTitle ${
                                            !phoneTab?.dyl_numbers?.length &&
                                            `disabled`
                                        }`}
                                    >
                                        DYL Numbers
                                    </span>
                                ),
                                icon: activeIndex?.includes?.(1)
                                    ? accordionIconUp
                                    : Object.assign(accordionIconDown, {
                                        disabled: !phoneTab?.dyl_numbers?.length,
                                    }),
                            },
                            content: {
                                content: (
                                    <Segment basic className="DylNumbers">
                                        <DylNumbers
                                            isPhoneTab
                                            dylNumbers={phoneTab?.dyl_numbers}
                                            dylNumbersCount={
                                                phoneTab?.dyl_numbers?.length
                                            }
                                            isReadingDylNumbers={isReadingPhoneTab}
                                        />
                                    </Segment>
                                ),
                            },
                        },
                        {
                            key: "externalNumbers",
                            title: {
                                content: (
                                    <span
                                        className={`AccordianTitle ${
                                            !phoneTab?.external_numbers?.length &&
                                            `disabled`
                                        }`}
                                    >
                                        External Numbers
                                    </span>
                                ),
                                icon: activeIndex?.includes?.(2)
                                    ? accordionIconUp
                                    : Object.assign(accordionIconDown, {
                                        disabled:
                                            !phoneTab?.external_numbers?.length,
                                    }),
                            },
                            content: {
                                content: (
                                    <Segment basic className="ExternalNumbers">
                                        <ExternalNumbers
                                            isPhoneTab
                                            external_numbers={
                                                phoneTab?.external_numbers
                                            }
                                            isReadingExternalNumbers={
                                                isReadingPhoneTab
                                            }
                                            external_numbers_count={
                                                phoneTab?.external_numbers?.length
                                            }
                                            selectVerifyingPhone={
                                                selectVerifyingPhone
                                            }
                                        />
                                    </Segment>
                                ),
                            },
                        },
                    ]}
                />
            </RestrictedView>
        </React.Fragment>
    );
};

export default PhoneTab;
